import { useLayoutEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { SecurityNavHit } from "@/shared/hooks/useSecurityNavSearch";
import {
  SEARCH_ACTIVE_FULL_BLEED_MAX_PX,
  SEARCH_RESULTS_MAX_HEIGHT_PX,
} from "@/shared/ui/appHeader/HeaderSearch.styles";
import { GRID_ROW_FONT_SIZE_PX } from "@/shared/ui/gridTableStyles";
import { DASH, formatPercent, formatResultCount } from "@/shared/utils/formatters";
import { classifyDateOnly } from "@/shared/utils/dateOnly";
import { measureTextWidth, type TextMeasureStyle } from "@/shared/utils/measureText";

export const HEADER_SEARCH_LIST_ID = "header-security-search-list";

/** Markets grid CUSIP link (`cusipLinkTextSx`). Body cells use `GRID_ROW_FONT_SIZE_PX`. */
const SEARCH_CUSIP_FONT_SIZE = "9.57px";
const SEARCH_BODY_FONT_SIZE = `${GRID_ROW_FONT_SIZE_PX}px`;
/** Desktop card. Fixed tracks; the name takes whatever is left. */
const WIDE_GRID_COLUMNS = "110px minmax(0, 1fr) 110px 80px 56px";
/** Keeps the last glyph off the next column when the probe rounds down. */
const TEXT_SLACK_PX = 2;

export function headerSearchOptionId(index: number): string {
  return `header-security-search-option-${index}`;
}

/** Maturity in the nav is the year only, matching the compact row in the design. */
export function formatMaturityYear(value: string | null | undefined): string {
  if (!value) return DASH;
  const parts = classifyDateOnly(value);
  if (parts?.kind === "date") return parts.yyyy;
  if (parts?.kind === "invalid") return DASH;
  const match = /^(\d{4})/.exec(value);
  return match?.[1] ?? DASH;
}

function displayText(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : DASH;
}

/** Two-letter words that are English, not a state code or an abbreviation such as GO. */
const LOWERCASE_SHORT_WORDS = new Set(["of", "in", "on", "at", "to", "by", "an", "or", "as", "is"]);

/** Muni acronyms that contain a vowel, so the no-vowel rule below does not catch them. */
const KNOWN_ACRONYMS = new Set(["mta", "usa", "ida", "isd", "usd", "pud", "mud", "cda", "eda"]);

/**
 * An issuer-name token that reads as an acronym: a two-letter code (GO, NY, ST),
 * a token with no vowels (NYC, HSG, MTG), or a known acronym (MTA).
 */
function isAcronym(word: string): boolean {
  if (!/^[a-z]+$/.test(word)) return false;
  if (word.length === 2) return !LOWERCASE_SHORT_WORDS.has(word);
  if (KNOWN_ACRONYMS.has(word)) return true;
  return word.length <= 4 && !/[aeiou]/.test(word);
}

/** Issuer names arrive in all caps. Show each word with a leading capital and keep acronyms upper case. */
export function toWordCase(value: string): string {
  if (value === DASH) return value;
  return value.toLowerCase().replace(/[a-z0-9']+/g, (word, offset: number) => {
    if (isAcronym(word)) return word.toUpperCase();
    if (offset > 0 && LOWERCASE_SHORT_WORDS.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

function resultCountLabel(
  totalCount: number,
  approximate: boolean,
  isLowerBound: boolean,
  shown: number,
): string {
  if (approximate) return shown === 1 ? "1 best match" : `${shown} best matches`;
  const noun = totalCount === 1 && !isLowerBound ? "result" : "results";
  return `${formatResultCount(totalCount, isLowerBound)} ${noun}`;
}

function statusLabel(isError: boolean, isLoading: boolean, idleLabel: string | undefined): string {
  if (isError) return "Search is unavailable";
  if (idleLabel) return idleLabel;
  if (isLoading) return "Searching…";
  return "No matching securities";
}

function probeTextWidth(text: string, style: Readonly<TextMeasureStyle>): number {
  return Math.ceil(measureTextWidth(text, style)) + TEXT_SLACK_PX;
}

/**
 * CUSIP, type, coupon, and year share one width per column — the widest text
 * in that column — so rows line up and the glyphs are not clipped. The name
 * takes the remaining space.
 */
function contentGridColumns(fontFamily: string, items: readonly SecurityNavHit[]): string {
  let cusip = 0;
  let type = 0;
  let coupon = 0;
  let year = 0;
  for (const item of items) {
    cusip = Math.max(
      cusip,
      probeTextWidth(displayText(item.cusip), {
        fontFamily,
        fontSize: SEARCH_CUSIP_FONT_SIZE,
        fontWeight: 700,
        letterSpacing: "0.02em",
      }),
    );
    type = Math.max(
      type,
      probeTextWidth(displayText(item.securityTypeDescription), {
        fontFamily,
        fontSize: SEARCH_BODY_FONT_SIZE,
        fontWeight: 400,
      }),
    );
    coupon = Math.max(
      coupon,
      probeTextWidth(formatPercent(item.couponRate), {
        fontFamily,
        fontSize: SEARCH_BODY_FONT_SIZE,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
      }),
    );
    year = Math.max(
      year,
      probeTextWidth(formatMaturityYear(item.maturityDate), {
        fontFamily,
        fontSize: SEARCH_BODY_FONT_SIZE,
        fontWeight: 400,
        fontVariantNumeric: "tabular-nums",
      }),
    );
  }
  return `${cusip}px minmax(0, 1fr) ${type}px ${coupon}px ${year}px`;
}

/** Side columns show their full text on a narrow card. The name still ellipsizes. */
function sideCellOverflow(fit: boolean) {
  if (fit) return { whiteSpace: "nowrap" as const };
  return {
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    whiteSpace: "nowrap" as const,
  };
}

/** Phones and narrow toolbars. The centered desktop card stays on the fixed tracks. */
function useFitResultColumns(items: readonly SecurityNavHit[]): string {
  const [columns, setColumns] = useState(WIDE_GRID_COLUMNS);

  useLayoutEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(`(max-width: ${SEARCH_ACTIVE_FULL_BLEED_MAX_PX}px)`);
    const apply = () => {
      if (!query.matches || items.length === 0) {
        setColumns(WIDE_GRID_COLUMNS);
        return;
      }
      const fontFamily = getComputedStyle(document.body).fontFamily;
      setColumns(contentGridColumns(fontFamily, items));
    };
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, [items]);

  return columns;
}

interface HeaderSearchResultsProps {
  items: SecurityNavHit[];
  totalCount: number;
  /** The total is a nearest-neighbour candidate count; the footer does not show it as a match count. */
  totalCountApproximate?: boolean;
  /** The server stopped counting early: the footer shows the total as "N+". */
  totalCountIsLowerBound?: boolean;
  activeIndex: number;
  isLoading: boolean;
  isError: boolean;
  /** Shown when the query has not been sent yet, for example a single character. */
  idleLabel?: string;
  onActiveIndex: (index: number) => void;
  onSelect: (cusip: string) => void;
}

export default function HeaderSearchResults({
  items,
  totalCount,
  totalCountApproximate = false,
  totalCountIsLowerBound = false,
  activeIndex,
  isLoading,
  isError,
  idleLabel,
  onActiveIndex,
  onSelect,
}: Readonly<HeaderSearchResultsProps>) {
  const showStatus = isError || items.length === 0;
  const status = statusLabel(isError, isLoading, idleLabel);
  const gridColumns = useFitResultColumns(items);
  const fitSideColumns = gridColumns !== WIDE_GRID_COLUMNS;

  return (
    <>
      <Box
        // Custom search listbox on a styled Box; native select can't host these result rows.
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
        role="listbox"
        id={HEADER_SEARCH_LIST_ID}
        aria-label="Securities"
        onMouseDown={(event) => {
          // Keep focus in the header field so a row click is not lost to blur.
          event.preventDefault();
        }}
        sx={(theme) => ({
          boxSizing: "border-box",
          width: "100%",
          minWidth: 0,
          maxHeight: SEARCH_RESULTS_MAX_HEIGHT_PX,
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.app.headerSearch.rowBorderColor} transparent`,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.app.headerSearch.rowBorderColor,
            borderRadius: 3,
          },
        })}
      >
        {showStatus ? (
          <Typography
            sx={(theme) => ({
              px: "16px",
              py: "12px",
              fontFamily: theme.typography.fontFamily,
              fontSize: "13px",
              fontWeight: 400,
              color: theme.app.headerSearch.yearColor,
            })}
          >
            {status}
          </Typography>
        ) : (
          items.map((item, index) => {
            const selected = index === activeIndex;
            return (
              <Box
                key={item.cusip}
                id={headerSearchOptionId(index)}
                // Option rendered as a styled Box; a native option can't host this row.
                // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
                role="option"
                aria-selected={selected}
                onMouseEnter={() => onActiveIndex(index)}
                onClick={() => onSelect(item.cusip)}
                sx={(theme) => ({
                  display: "grid",
                  gridTemplateColumns: gridColumns,
                  columnGap: "14px",
                  alignItems: "center",
                  boxSizing: "border-box",
                  width: "100%",
                  minWidth: 0,
                  px: "16px",
                  height: 43,
                  cursor: "pointer",
                  borderBottom: `1px solid ${theme.app.headerSearch.rowBorderColor}`,
                  backgroundColor: selected
                    ? theme.app.headerSearch.activeRowBackground
                    : "transparent",
                  "&:hover": {
                    backgroundColor: theme.app.headerSearch.activeRowBackground,
                  },
                })}
              >
                <Box
                  component="span"
                  sx={(theme) => ({
                    fontFamily: theme.typography.fontFamily,
                    fontSize: SEARCH_CUSIP_FONT_SIZE,
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                    color: theme.app.headerSearch.cusipColor,
                    ...sideCellOverflow(fitSideColumns),
                  })}
                >
                  {displayText(item.cusip)}
                </Box>
                <Box
                  component="span"
                  sx={(theme) => ({
                    fontFamily: theme.typography.fontFamily,
                    fontSize: SEARCH_BODY_FONT_SIZE,
                    fontWeight: 700,
                    color: theme.app.headerSearch.nameColor,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  })}
                >
                  {toWordCase(displayText(item.issuerName))}
                </Box>
                <Box
                  component="span"
                  sx={(theme) => ({
                    fontFamily: theme.typography.fontFamily,
                    fontSize: SEARCH_BODY_FONT_SIZE,
                    fontWeight: 400,
                    color: theme.app.headerSearch.typeColor,
                    ...sideCellOverflow(fitSideColumns),
                  })}
                >
                  {displayText(item.securityTypeDescription)}
                </Box>
                <Box
                  component="span"
                  sx={(theme) => ({
                    fontFamily: theme.typography.fontFamily,
                    fontVariantNumeric: "tabular-nums",
                    fontSize: SEARCH_BODY_FONT_SIZE,
                    fontWeight: 700,
                    textAlign: "right",
                    color: theme.app.headerSearch.couponColor,
                    ...sideCellOverflow(fitSideColumns),
                  })}
                >
                  {formatPercent(item.couponRate)}
                </Box>
                <Box
                  component="span"
                  sx={(theme) => ({
                    fontFamily: theme.typography.fontFamily,
                    fontVariantNumeric: "tabular-nums",
                    fontSize: SEARCH_BODY_FONT_SIZE,
                    fontWeight: 400,
                    textAlign: "right",
                    color: theme.app.headerSearch.yearColor,
                    ...sideCellOverflow(fitSideColumns),
                  })}
                >
                  {formatMaturityYear(item.maturityDate)}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
      <Box
        sx={(theme) => ({
          px: "16px",
          py: "12px",
          fontFamily: theme.typography.fontFamily,
          fontSize: "11px",
          fontWeight: 400,
          color: theme.app.headerSearch.footerColor,
        })}
      >
        {`${resultCountLabel(totalCount, totalCountApproximate, totalCountIsLowerBound, items.length)} · Esc to close`}
      </Box>
    </>
  );
}
